<template>
  <v-sheet class="custom-calendar-container custom-day-calendar-container fill-height is-relative">
    <div class="pb-2">
      <zoom-buttons></zoom-buttons>
    </div>
    <FullCalendar
      :class="`calendar-view-${$viewType.toLowerCase()}`"
      :data-testid="`calendar-view-${$viewType.toLowerCase()}-container`"
      ref="fullCalendar"
      :options="localCalendarOptions"
      :data-zoom-level="$zoomLevel"
      :data-interval-setting="$intervalMinutes"
      :resourceAreaWidth="'auto'">
      <template #slot-lane-content="calendarSlotInfo">
        <div class="d-flex reserve-buttons-wrapper">
          <div
            class="d-flex justify-end slot"
            v-for="resource in resources"
            :style="getReserveWrapperStyle(resource.id)"
            :key="`slot-${calendarSlotInfo.date}-resource-${resource.id}-${selectedDate}`">
            <quick-reserve-button
              v-if="$areEventsReady"
              :data-resource-id="resource.id"
              :dock-id="resource.id"
              :timezone="timezone"
              :loading="creatingReserve"
              :date-object="calendarSlotInfo.date"
              :interval-minutes="intervalMinutesInt"
              :start="getQuickReserveStartTime(calendarSlotInfo)"
              @create-reserve="createReserve"></quick-reserve-button>
          </div>
        </div>
      </template>
      <template #resource-label-content="resourceLabelInfo">
        <div
          class="d-flex full-width align-center full-height category-header"
          :class="getGroupClasses(resourceLabelInfo.resource.id)">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <span v-bind="props" :class="getDockNameClasses(resourceLabelInfo.resource.id)">
                {{ getDockNameById(resourceLabelInfo.resource.id) }}
              </span>
            </template>
            <span v-if="$areEventsReady">
              {{ totalAppointmentsByDockString($docksKeyedById[resourceLabelInfo.resource.id]) }}
            </span>
          </v-tooltip>
          <v-btn
            v-if="novaCore.isCapacityParent($docksKeyedById[resourceLabelInfo.resource.id])"
            variant="text"
            density="compact"
            :data-testid="`grid-${resourceLabelInfo.resource.id}-capacity-toggle-button`"
            class="font-size-x-small plain-text pa-0 d-flex align-center text-black capacity-view-toggle"
            @click="toggleCapacityDocks(resourceLabelInfo.resource.id)">
            <span v-if="!shouldShowCapacityDockOpts[resourceLabelInfo.resource.id]">
              {{ getDockCapacityCount(resourceLabelInfo.resource.id) }}
            </span>
            <v-icon
              size="x-small"
              class="ml-1"
              v-if="!shouldShowCapacityDockOpts[resourceLabelInfo.resource.id]">
              mdi-arrow-expand-right
            </v-icon>
            <v-icon size="x-small" class="ml-1" v-else>mdi-arrow-expand-left</v-icon>
          </v-btn>
        </div>
      </template>
      <template #event-content="eventContentInfo">
        <div
          v-if="eventContentInfo.event.display !== 'background'"
          class="event-content"
          :key="`${renderKey}-${eventContentInfo.event.extendedProps.id}`"
          :data-weekday="eventContentInfo.event.extendedProps.weekday"
          :data-event-id="eventContentInfo.event.id"
          :data-status="eventContentInfo.event.extendedProps.status.toLowerCase()"
          :data-time="eventContentInfo.event.extendedProps.startTime24"
          :data-date="eventContentInfo.event.extendedProps.startDate"
          :data-dock-id="eventContentInfo.event.extendedProps.dockId">
          <event-with-tooltip
            :is-light-theme="$useLightGridTheme"
            :event="{
              // fullcalendar nests any 'non standard' properties under extendedProps
              ...eventContentInfo.event.toPlainObject(),
              ...eventContentInfo.event.extendedProps
            }"
            :enableMilitaryTime="$isMilitaryTimeEnabled($selectedWarehouse)"
            :view-type="CalendarViewEnum.DAY"
            :use-new-grid-tiles="useNewGridTilesSetting"
            @cancel-reserve="cancelReserve"
            :disable-tooltip="
              isTooltipDisabled(eventContentInfo.event.extendedProps)
            "></event-with-tooltip>
        </div>

        <div
          v-if="
            isStartingStatus(eventContentInfo.event.extendedProps.status) &&
            $rolePermissions.canUpdateAppointment
          "
          class="v-event-drag-bottom"
          @mousedown.stop="extendBottom(eventContentInfo.event.extendedProps)"></div>
      </template>
    </FullCalendar>

    <load-type-re-select-dialog
      v-if="eventDropInfo"
      @close="nevermindLoadTypeDialog"
      :original-event="eventDropInfo.oldEvent.toPlainObject()"
      :original-event-dock="$docksKeyedById[eventDropInfo.oldResource.id]"
      :event="eventDropInfo.event.toPlainObject()"
      :event-warehouse="$selectedWarehouse"
      :event-dock="$docksKeyedById[eventDropInfo.newResource.id]"
      :show-dialog="shouldShowLoadTypeReselectDialog" />

    <calendar-dialogs
      :appointment-context="appointmentContext"
      :show-create-dialog="showCreateDialog"
      :show-details-dialog="showDetailsDialog"
      :show-reserve-dialog="showReserveDialog"
      @close="handleDialogClose" />

    <event-context-menu
      v-if="eventContextMenuEvent"
      @close="clearContextMenuData"
      :nativeEvent="eventContextMenuEvent"
      :appointment="{
        ...localEvents.find(event => event.id === eventContextMenuId)
      }" />
  </v-sheet>
</template>

<script>
import calendarMixin from '@/modules/calendar/mixins/calendarMixin';
import { CalendarViewEnum } from '@/enums';
import FullCalendar from '@fullcalendar/vue3';
import { DateTime, Interval } from 'luxon';
import { isEqual, padStart } from 'lodash';
import { isRescheduleAllowed } from '@nova/core';
import luxonPlugin from '@fullcalendar/luxon2';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGrid from '@fullcalendar/scrollgrid';

/**
 * Calendar Day View
 * @displayName Calendar Day View
 */
export default {
  name: 'CalendarDayView',
  mixins: [calendarMixin],
  components: {
    FullCalendar
  },
  computed: {
    localCalendarOptions() {
      return {
        ...this.calendarOptions,
        contentHeight: 'auto',
        initialView: 'resourceTimeGridDay',
        stickyFooterScrollbar: true,
        plugins: [
          luxonPlugin,
          dayGridPlugin,
          interactionPlugin,
          resourceTimeGridPlugin,
          scrollGrid
        ],
        dayMinWidth: 250,
        timeZone: this.$selectedWarehouse?.timezone,
        events: this.localEvents,
        resourceOrder: 'order',
        resources: this.resources,
        resourcesSet: resources => {
          document.querySelectorAll('.fc-resource').forEach(el => {
            el.classList.remove('open-resource-group');
          });
          const groups = {};
          resources.forEach(resource => {
            const dock = this.$docksKeyedById[resource.id];
            if (!dock.capacityParentId) {
              return false;
            }
            if (this.shouldShowCapacityDockOpts[dock.capacityParentId]) {
              if (!Object.prototype.hasOwnProperty.call(groups, dock.capacityParentId)) {
                groups[dock.capacityParentId] = [];
              }
              groups[dock.capacityParentId].push(resource);
            }
          });
          Object.keys(groups).forEach(parentId => {
            const group = groups[parentId];
            group.forEach(resource => {
              const els = document.querySelectorAll(
                `.fc-resource[data-resource-id="${resource.id}"]`
              );
              els.forEach(el => {
                el.classList.add('open-resource-group');
              });
            });
          });
        },
        slotEventOverlap: false,
        viewDidMount: () => {
          this.addCustomClassesToSlotLabels();
          this.ready = true;
        },
        eventDidMount: this.handleEventDidMount
      };
    },
    CalendarViewEnum() {
      return CalendarViewEnum;
    },
    localEvents() {
      const localEvents = this.events.map(event => {
        // Set the resource ID where capacity docks have their parent dock ID unless expanded
        event.resourceId = !this.shouldShowCapacityDockOpts[event.parentDockId]
          ? event.parentDockId ?? event.dockId
          : event.dockId;
        event.resourceEditable = true; // Always allow ability to change dock

        // Editing rules for drag/drop and duration
        if (isRescheduleAllowed(event.status)) {
          event.durationEditable = true;
          event.startEditable = true;
          event.resourceEditable = true;
        } else {
          event.startEditable = false;
          event.durationEditable = false;
        }
        return event;
      });

      return localEvents;
    },
    eventsKeyedByResource() {
      const eventsByResource = {};
      this.localEvents.forEach(event => {
        if (!Object.prototype.hasOwnProperty.call(eventsByResource, event.resourceId)) {
          eventsByResource[event.resourceId] = [];
        }
        eventsByResource[event.resourceId].push(event);
      });

      return eventsByResource;
    }
  },
  data() {
    return {
      creatingReserve: false,
      resources: [],
      localDocks: []
    };
  },
  methods: {
    setResources() {
      let selectedDocks = this.novaCore.naturalSort(this.localDocks, false, 'name');
      selectedDocks = this.novaCore.naturalSort(selectedDocks, false, 'sortOrder');

      const resources = selectedDocks.map((dock, index) => {
        return {
          id: dock.id,
          title: dock.name,
          order: index + 1,
          businessHours: this.getDayBusinessHoursForDock(dock)
        };
      });
      this.resources = resources;
    },
    handleEventAllow(info, event) {
      let isValid = false;
      const isResize = event.startStr === info.startStr && event.endStr !== info.endStr;
      const isValidSnapTime = isResize && this.snapOverride ? true : this.isSnapTime(info.startStr);
      if (!isValidSnapTime) {
        return false;
      }

      const dock = this.$docksKeyedById[info.resource.id];
      const isDockExpanded = this.shouldShowCapacityDockOpts[dock.id];
      const dockEvents = (this.eventsKeyedByResource[info.resource.id] ?? []).filter(dockEvent => {
        return (
          dockEvent.id !== event.id &&
          dockEvent.status !== this.novaCore.AppointmentStatus.Cancelled
        );
      });
      const dockCapacity = dock.capacityChildren?.length ?? 1;

      let startOverlaps = 0;
      let endOverlaps = 0;
      const startDateTime = DateTime.fromISO(info.startStr);
      const endDateTime = DateTime.fromISO(info.endStr).minus({ minute: 1 });
      dockEvents.forEach(dockEvent => {
        const interval = Interval.fromDateTimes(
          DateTime.fromISO(dockEvent.start),
          DateTime.fromISO(dockEvent.end)
        );
        if (interval.contains(startDateTime)) {
          startOverlaps++;
        }
        if (interval.contains(endDateTime)) {
          endOverlaps++;
        }
      });

      if (!isDockExpanded && dockCapacity > 1) {
        isValid = startOverlaps < dockCapacity && endOverlaps < dockCapacity;
      } else {
        isValid = startOverlaps < 1 && endOverlaps < 1;
      }

      return isValid;
    },
    /**
     * Used to get the resource (Dock) frame and apply the width to the quick reserve wrappers
     * @param resourceId
     * @returns {{width: string}}
     */
    getReserveWrapperStyle(resourceId) {
      const resourceFrame = document.querySelector(
        '.fc-resource[data-resource-id="' + resourceId + '"]'
      );
      const width = resourceFrame.getBoundingClientRect().width;
      return {
        width: `${width}px`
      };
    },
    getDockNameById(dockId) {
      const dock = this.$docksKeyedById[dockId];

      if (dock) {
        return this.novaCore.isCapacityChild(dock) ? dock.readableDockName : dock.name;
      }
      return '';
    },
    getDockNameClasses(dockId) {
      const dock = this.$docksKeyedById[dockId];

      if (dock) {
        return {
          'flex-grow-1': true,
          'append-animate': this.novaCore.isCapacityChild(dock),
          'text-center': !this.novaCore.isCapacityParticipant(dock),
          'justify-space-between': true
        };
      }

      return {};
    },
    handleCalendarClick(dateClickInfo) {
      const clickTarget = dateClickInfo.jsEvent.target;
      const classNamesToCheck = ['mdi', 'fc-non-business'];
      if (classNamesToCheck.some(className => clickTarget.classList.contains(className))) {
        return;
      }
      if (!this.$selectedWarehouse?.id) {
        this.$eventHub.$emit('shake-element', 'warehouse-select');
        return;
      }

      let dock = this.$docksKeyedById[dateClickInfo.resource.id];

      if (dock) {
        if (this.novaCore.isCapacityChild(dock)) {
          dock = this.$docksKeyedById[dock.capacityParentId];
        }
        this.showCreateAppointmentDialog(dateClickInfo, dock);
      } else {
        this.$eventHub.$emit('shake-element', 'dock-select');
      }
    },
    async createReserve(data) {
      data.e.preventDefault();
      this.$emit('set-loader', true);
      data.duration_min = this.intervalMinutesInt;
      let dockIdsToReserve = [data.dockId];
      const dock = this.localDocks.find(dock => dock.id === data.dockId);
      const isParentDock = this.novaCore.isCapacityParent(dock);
      // If it's a parent dock and capacity is NOT expanded, we should reserve all capacity
      if (isParentDock && !this.shouldShowCapacityDockOpts[dock.id]) {
        dockIdsToReserve = dock.capacityChildren.map(child => child.id);
      }
      this.creatingReserve = true;

      const promises = [];
      dockIdsToReserve.forEach(dockId => {
        data.dockId = dockId;
        promises.push(this.$store.dispatch('Appointments/createReserve', data));
      });
      try {
        await Promise.all(promises);
      } finally {
        this.$emit('set-loader', false);
        this.creatingReserve = false;
      }
    },
    getDockCapacityCount(dockId) {
      const count = this.$docksKeyedById?.[dockId].capacityChildren?.length;
      return count ?? 0;
    },
    toggleCapacityDocks(dockId) {
      this.shouldShowCapacityDockOpts[dockId] = !this.shouldShowCapacityDockOpts[dockId];
    },
    getGroupClasses(dockId) {
      let classes = '';
      const dock = this.$docksKeyedById[dockId];
      if (dock) {
        if (this.novaCore.isCapacityChild(dock)) {
          classes = 'category-group child append-animate';
        }
        if (this.novaCore.isCapacityParent(dock) && this.shouldShowCapacityDockOpts[dockId]) {
          classes += 'category-group parent append-animate';
        }
      }

      return classes;
    },
    insertDocksIntoGrid(selectedDockIndex, selectedDock) {
      const docks = [...this.localDocks];
      const isSelectedParent =
        selectedDockIndex !== -1 && this.novaCore.isCapacityParent(selectedDock);
      // add the children in the position next to its parent
      selectedDockIndex++;
      if (isSelectedParent) {
        this.novaCore.sortCapacityChildren(selectedDock).forEach(child => {
          const childIndex = this.localDocks.findIndex(dock => dock.id === child.id);
          // This condition ignores the parent dock which is included in the child dock array by design
          if (this.novaCore.isCapacityChild(child) && childIndex === -1) {
            docks.splice(selectedDockIndex, 0, child);
            selectedDockIndex++;
          }
        });
      }
      this.localDocks = docks;
    },
    removeDockFromGrid(selectedDock) {
      const docks = [...this.localDocks];
      const hasChildDocks = selectedDock?.capacityChildren?.length > 0;
      if (hasChildDocks) {
        selectedDock?.capacityChildren.forEach(child => {
          if (this.novaCore.isCapacityChild(child)) {
            const childIndex = docks.findIndex(dock => dock.id === child.id);
            if (childIndex !== -1) {
              docks.splice(childIndex, 1);
            }
          }
        });
      }
      this.localDocks = docks;
    },
    totalAppointmentsByDock(dock) {
      if (dock?.id) {
        return (
          this.localEvents?.filter(event => {
            const notReserve = !this.novaCore.isReserve(event);
            const notCanceled = event.status !== this.novaCore.AppointmentStatus.Cancelled;
            const selectedDateString = DateTime.fromISO(this.$selectedDate)
              .setZone(this.timezone)
              .toISO()
              .split('T')[0];
            const todayEvent =
              DateTime.fromISO(event.start)
                .setZone(this.timezone)
                .toISO()
                .startsWith(selectedDateString) ||
              (DateTime.fromISO(event.end)
                .setZone(this.timezone)
                .toISO()
                .startsWith(selectedDateString) &&
                !event.end.endsWith('00:00'));
            const validAppointment = notReserve && notCanceled && todayEvent;

            const dockAppointment = event.dockId === dock.id;
            const parentAppointment = Boolean(event.parentDockId) && event.parentDockId === dock.id;
            const isParentDock = this.novaCore.isCapacityParent(dock);
            const isCapacityVisible = !this.shouldShowCapacityDockOpts[dock.id];

            if (isParentDock && isCapacityVisible) {
              return (parentAppointment || dockAppointment) && validAppointment;
            }

            return validAppointment && dockAppointment;
          }).length || 0
        );
      }

      return 0;
    },
    totalAppointmentsByDockString(dock) {
      const total = this.totalAppointmentsByDock(dock);
      return `${total} ${this.$t('global.specialtyTerms.appointments', total)}`;
    },
    getEntityScheduleDays(entity) {
      return Object.keys(entity.schedule ?? {}).filter(key => key !== 'version');
    },
    /**
     * Gets the intersected Warehouse/Dock business hours for the current day
     * @param dock
     * @returns {*}
     */
    getDayBusinessHoursForDock(dock) {
      const warehouseScheduleDays = this.getEntityScheduleDays(this.$selectedWarehouse);
      const dockScheduleDays = this.getEntityScheduleDays(dock);

      const warehouseHasSchedule =
        this.$selectedWarehouse.schedule && warehouseScheduleDays.length > 0;
      const dockHasSchedule = dock.schedule && dockScheduleDays.length > 0;

      // If both schedules are null, return wide-open
      if (!warehouseHasSchedule && !dockHasSchedule) {
        return [];
      }

      const warehouseSchedule =
        warehouseScheduleDays.length > 0
          ? this.$selectedWarehouse.schedule
          : this.novaCore.getFullyOpenSchedule();
      const dockSchedule =
        dockScheduleDays.length > 0 ? dock.schedule : this.novaCore.getFullyOpenSchedule();

      const isWarehouseClosed =
        warehouseSchedule?.closedIntervals?.filter(interval => {
          return interval.start === this.selectedDateTime.toISO();
        }).length > 0;

      const isDockClosed =
        dockSchedule?.closedIntervals?.filter(interval => {
          return interval.start === this.selectedDateTime.toISO();
        }).length > 0;

      const currentDayOfWeek = DateTime.fromISO(this.$selectedDate)
        .setZone(this.timezone)
        .weekdayLong.toLowerCase();
      const intersectedSchedule = this.mergeWarehouseAndDockDaySchedule(
        warehouseSchedule?.[currentDayOfWeek] ?? [],
        dockSchedule?.[currentDayOfWeek] ?? []
      );

      // If no hours or warehouse/dock is closed, return empty array
      if (!intersectedSchedule.length || isWarehouseClosed || isDockClosed) {
        return {
          daysOfWeek: []
        };
      }

      const dayOfWeek = this.selectedDateTime.weekday;
      return intersectedSchedule.map(schedule => {
        if (schedule.end === '23:59') {
          schedule.end = '24:00';
        }
        return {
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6].filter(day =>
            isWarehouseClosed || isDockClosed ? day !== dayOfWeek : true
          ),
          startTime: padStart(schedule.start, 5, '0'),
          endTime: padStart(schedule.end, 5, '0')
        };
      });
    },
    getQuickReserveStartTime(calendarSlotInfo) {
      const dateTimeOpts = { zone: this.timezone, setZone: true };
      return DateTime.fromFormat(
        `${DateTime.fromISO(this.$selectedDate)
          .setZone(this.timezone)
          .toFormat('yyyy-MM-dd')} ${DateTime.fromJSDate(
          calendarSlotInfo.date,
          dateTimeOpts
        ).toFormat('HH:mm:ss')}`,
        'yyyy-MM-dd HH:mm:ss',
        dateTimeOpts
      )
        .toUTC()
        .toISO();
    },
    setshouldShowCapacityDockOpts() {
      const shouldShowCapacityDockOpts = {};
      this.$selectedDocks.forEach(dock => {
        shouldShowCapacityDockOpts[dock.id] = false;
      });
      this.shouldShowCapacityDockOpts = {
        ...shouldShowCapacityDockOpts,
        ...this.shouldShowCapacityDockOpts
      };
    }
  },
  async mounted() {
    this.calendarApi.gotoDate(this.$selectedDate);
    if (!this.localDocks?.length) {
      this.localDocks = [...this.docks];
    }
    this.setResources();
    this.localDocks = [...this.docks];

    this.calendarApi.gotoDate(this.$selectedDate);
    this.setshouldShowCapacityDockOpts();
    this.mounted = true;
  },

  watch: {
    docks(newVal) {
      this.localDocks = [...newVal];
    },
    localDocks(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.setshouldShowCapacityDockOpts();
        this.setResources();
      }
    },
    $selectedDate() {
      if (this.mounted) {
        this.calendarApi.gotoDate(this.$selectedDate);
        this.setResources();
      }
    },
    $selectedWarehouse: {
      async handler() {
        if (this.mounted) {
          this.shouldShowCapacityDockOpts = {};
          this.setResources();
        }
      },
      immediate: true
    },
    shouldShowCapacityDockOpts: {
      handler(newDocks, oldDocks) {
        this.$emit('is-dock-capacity-toggle', true);
        Object.entries(newDocks).forEach(([dockId, shouldShow]) => {
          const selectedDock = this.$docksKeyedById[dockId];
          if (selectedDock) {
            let selectedDockIndex = this.localDocks.findIndex(dock => dock.id === selectedDock.id);
            if (shouldShow) {
              this.insertDocksIntoGrid(selectedDockIndex, selectedDock);
            } else {
              this.removeDockFromGrid(selectedDock);
            }
          }
        });
        if (
          newDocks &&
          oldDocks &&
          Object.keys(newDocks)?.length !== Object.keys(oldDocks)?.length
        ) {
          this.$emit('is-dock-capacity-toggle', true);
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>
